import { Button as AntButton, Checkbox, Input, Select } from "antd"
import { useEffect, useMemo, useRef, useState } from "react"
import { Button as BootstrapButton, Col, Form, Row } from "react-bootstrap"
import { AddressComponent } from "../Common/AddressAutocomplete"
import CheckGroup, { CheckGroupWithImage } from "../Common/components/CheckGroup"
import { DollarInputV2, moneyTypingValue } from "./DollarInputV2"
import EditableTable from "../Common/components/tables"
import { Tips } from "../Common/Tooltip"
import * as com from "../Common"
import * as st from "../State"
import "./styles.css"

const FieldError = ({ message }) => (
    message ? <div className="warning-msg">{message}</div> : null
)

// DOM / original check order for scroll-to-first
const FIELD_ORDER = [
    "purpose",
    "occupancy",
    "propertytype",
    "numberofunits",
    "hascontract",
    "address",
    "loanbalance",
    "appraisal",
    "salesprice",
    "refinanceFreeAndClear",
    "purposeAmount",
]

const scrollToField = (fieldKey) => {
    if (!fieldKey || typeof document === "undefined") return
    // Free-and-clear refinance message is shown under the address block.
    const scrollKey = fieldKey === "refinanceFreeAndClear" ? "address" : fieldKey
    const el = document.querySelector(`[data-field="${scrollKey}"]`)
    if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "center" })
    }
}

const emptyMobileHomeInfo = {
    mobilehomeparkfullname: "",
    parkAddress: "",
    leasedSpaceRentAmount: "",
    widthft: "",
    widthinch: "",
    lengthft: "",
    lengthinch: "",
    year: "",
    space: "",
    mobilehomedescription: "",
    mobilehometype: "",
    mobilehomelocation: "",
    originalpurchaseprice: "",
    manufacturerMake: "",
    manufacturerModel: "",
    cashOutRequested: "",
    debtsToBePaid: [],
}

const emptyProperty = {
    address: "",
    unit: "",
    city: "",
    state: "",
    county: "",
    zipcode: "",
    purpose: "",
    hascontract: null,
    propertytype: "",
    numberofunits: "",
    appraisal: "",
    salesprice: "",
    status: "",
    occupancy: "",
    isMixedUse: false,
    qualifiesForFHAsecondary: false,
    isManufacturedHome: false,
    insurancemaintenancetaxes: "",
    grossrentalincome: "",
    netrentalincome: "",
    mobilehomeinfo: emptyMobileHomeInfo,
}
const PROPERTY_ADDRESS_PATTERN = "^[0-9]+\\s[\\p{L}\\p{N} !:,&()'?#+\\/@-]+$"

const emptySelection = {
    downpayment: "",
    paydown: "",
    cashout: "",
    loansize: "",
    mincashout: "",
}

const normalizeProperty = (property = {}) => ({
    ...emptyProperty,
    ...property,
    hascontract: typeof property.hascontract === "boolean" ? property.hascontract : null,
    propertytype: property.propertytype || property.propertyType || "",
    isManufacturedHome: property.isManufacturedHome === true || property.propertytype === "mobilehome" || property.propertyType === "mobilehome",
    mobilehomeinfo: {
        ...emptyMobileHomeInfo,
        ...(property.mobilehomeinfo || {}),
        debtsToBePaid: Array.isArray(property.mobilehomeinfo?.debtsToBePaid) ? property.mobilehomeinfo.debtsToBePaid : [],
    },
})

const boolToYesNo = (value) => (value === true ? "yes" : value === false ? "no" : "")
const yesNoToBool = (value) => value === "yes"
const loanType = (loan) => `${loan?.creditLoanType || loan?.creditloantype || ""}`.toLowerCase()
const isMortgageLoan = (loan) => ["mortgage", "homeequity", "realestate"].some((keyword) => loanType(loan).includes(keyword))
const remainingBalance = (loan) => `${loan?.remainingBalance || loan?.remainingbalance || ""}`
const needsMortgageBalance = (purpose, loans = []) => {
    if (purpose === "purchase") {
        return false
    }
    return !loans.some((loan) => (
        isMortgageLoan(loan) &&
        loan.forSubjectProperty === true &&
        remainingBalance(loan) !== ""
    ))
}
const totalSubjectMortgageBalance = (loans = []) => (
    loans
        .filter((loan) => isMortgageLoan(loan) && loan.forSubjectProperty === true)
        .reduce((sum, loan) => sum + (parseInt(remainingBalance(loan) || "0", 10) || 0), 0)
)

const PropertyV2 = ({ application, primaryParticipant, liabilities, saving, onSave, onBack }) => {
    const [property, setProperty] = useState(emptyProperty)
    const [loanbalance, setLoanbalance] = useState("")
    const [financingsameproperty, setFinancingsameproperty] = useState(null)
    const [firsttimebuyer, setFirsttimebuyer] = useState(null)
    const [selection, setSelection] = useState(emptySelection)
    const [hasNewMortgages, setHasNewMortgages] = useState(null)
    const [newMortgages, setNewMortgages] = useState([])
    const [fieldErrors, setFieldErrors] = useState({})
    const hasNewMortgagesRef = useRef(null)
    const newMortgagesRef = useRef([])

    useEffect(() => {
        setProperty(normalizeProperty(application?.property))
        setLoanbalance(
            application?.loanbalance == null || application?.loanbalance === ""
                ? ""
                : `${application.loanbalance}`,
        )
        setFinancingsameproperty(typeof application?.financingsameproperty === "boolean" ? application.financingsameproperty : false)
        setFirsttimebuyer(typeof application?.firsttimebuyer === "boolean" ? application.firsttimebuyer : null)
        setSelection({ ...emptySelection, ...(application?.selection || {}) })
        const loadedNewMortgages = Array.isArray(liabilities?.newMortgages) ? liabilities.newMortgages : []
        setNewMortgages(loadedNewMortgages)
        newMortgagesRef.current = loadedNewMortgages
        // Legacy empty list renders as No.
        const loadedHasNewMortgages = loadedNewMortgages.length > 0
        setHasNewMortgages(loadedHasNewMortgages)
        hasNewMortgagesRef.current = loadedHasNewMortgages
        setFieldErrors({})
    }, [application, liabilities])

    const clearFieldError = (field) => {
        setFieldErrors((prev) => {
            if (!prev[field]) return prev
            const next = { ...prev }
            delete next[field]
            return next
        })
    }

    const setField = (field, value) => {
        setProperty((prev) => ({ ...prev, [field]: value }))
        if (field === "occupancy") clearFieldError("occupancy")
        if (field === "address") {
            clearFieldError("address")
            clearFieldError("refinanceFreeAndClear")
        }
        if (field === "appraisal") clearFieldError("appraisal")
        if (field === "salesprice") clearFieldError("salesprice")
    }
    const setMobileHomeField = (field, value) => setProperty((prev) => ({
        ...prev,
        mobilehomeinfo: {
            ...emptyMobileHomeInfo,
            ...(prev.mobilehomeinfo || {}),
            [field]: value,
        },
    }))
    const setMobileHomeAddressField = (field, value) => setProperty((prev) => {
        const info = { ...emptyMobileHomeInfo, ...(prev.mobilehomeinfo || {}), [field]: value || "" }
        const legacy = [info.parkStreet, info.parkUnit, info.parkCity, info.parkState, info.parkZip].filter(Boolean).join(", ")
        return { ...prev, mobilehomeinfo: { ...info, parkAddress: legacy } }
    })
    const setPropertyType = (value) => {
        const multifamily = value === "twotofourfamily" || value === "commercial"
        setProperty((prev) => {
            const wasMultifamily = prev.propertytype === "twotofourfamily" || prev.propertytype === "commercial"
            // Not multifamily: one unit. Coming into multifamily from another type: ask.
            const numberofunits = multifamily ? (wasMultifamily ? prev.numberofunits : "") : "1"
            return { ...prev, propertytype: value, numberofunits, isManufacturedHome: value === "mobilehome" }
        })
        clearFieldError("propertytype")
        clearFieldError("numberofunits")
    }
    const setSelectionField = (field, value) => {
        setSelection((prev) => ({ ...prev, [field]: value || "" }))
        clearFieldError("purposeAmount")
    }
    const setLoanbalanceValue = (value) => {
        setLoanbalance(value == null || value === "" ? "" : `${value}`)
        clearFieldError("loanbalance")
    }
    // Multifamily: the unit count is asked only here. "Multifamily (1-4 unit)"
    // is the 2-4 family type; a five-or-more-unit building is stored as
    // "commercial" (the closest value the loan state accepts: an unknown
    // value would be reset by the state reader and alert the loan officer).
    const isSmallMultifamily = property.propertytype === "twotofourfamily"
    const isLargeMultifamily = property.propertytype === "commercial"
    const isMultifamily = isSmallMultifamily || isLargeMultifamily
    // PUD is one card for both loan-state values.
    const propertyTypeCardValue = ["pudattached", "puddetached"].includes(property.propertytype) ? "puddetached" : property.propertytype
    const isPurchase = property.purpose === "purchase"
    const isRefinance = property.purpose === "refinance"
    const isCashout = property.purpose === "cashoutrefinance"
    const isPreapproval = property.hascontract === false && isPurchase
    const shouldAskMortgageBalance = needsMortgageBalance(property.purpose, liabilities?.loans)
    const showFinancingSameProperty = !isPurchase && primaryParticipant?.residenceOwnership === st.O_Own
    const secondHomeLabel = com.getCustomerId() === "mobilehomeloans" ? "Secondary / Vacation" : "Secondary residence"
    const occupancyOptions = useMemo(() => ([
        { value: "principal", label: "Primary residence" },
        { value: "secondhome", label: secondHomeLabel },
        { value: "investment", label: "Investment" },
        ...(com.getCustomerId() === "mobilehomeloans"
            ? [{ value: "buyforsomeoneelse", label: "I'm buying the property for someone else" }]
            : []),
    ]), [secondHomeLabel])

    const copyResidenceToProperty = () => {
        const present = primaryParticipant?.presentAddress || {}
        setProperty((prev) => ({
            ...prev,
            address: present.street || "",
            unit: present.unit || "",
            city: present.city || "",
            state: present.state || "",
            county: present.county || "",
            zipcode: present.zip || "",
        }))
        clearFieldError("address")
        clearFieldError("refinanceFreeAndClear")
    }

    const handleFinancingSameProperty = (checked) => {
        setFinancingsameproperty(checked)
        // Owned-property list sync remains deferred (plan §8.3); only copy address like legacy checkbox path.
        if (checked) {
            copyResidenceToProperty()
        }
    }

    const setNewMortgageAnswer = (hasLoans, loans) => {
        hasNewMortgagesRef.current = hasLoans
        newMortgagesRef.current = loans
        setHasNewMortgages(hasLoans)
        setNewMortgages(loans)
    }
    const addNewMortgage = () => setNewMortgages((items) => {
        const nextItems = [...items, {
        key: `${Date.now()}-${Math.random()}`,
        creditor: "",
        lienType: "firstlien",
        monthlyAmount: "",
        loanAmount: "",
        creditLimit: "",
        }]
        newMortgagesRef.current = nextItems
        hasNewMortgagesRef.current = true
        setHasNewMortgages(true)
        return nextItems
    })
    const updateNewMortgage = (index, patch) => setNewMortgages((items) => {
        const nextItems = items.map((item, itemIndex) => itemIndex === index ? { ...item, ...patch } : item)
        newMortgagesRef.current = nextItems
        return nextItems
    })

    const purposeAmountField = isPurchase ? "downpayment" : isRefinance ? "paydown" : "cashout"

    const buildSelectionForSave = () => {
        const next = { ...selection }
        const appraisalOrSales = parseInt(property.salesprice || property.appraisal || "0", 10) || 0
        const down = parseInt(next.downpayment || "0", 10) || 0
        const paydown = parseInt(next.paydown || "0", 10) || 0
        const cashout = parseInt(next.cashout || "0", 10) || 0
        const mortgagebalance = shouldAskMortgageBalance
            ? (parseInt(loanbalance || "0", 10) || 0)
            : totalSubjectMortgageBalance(liabilities?.loans || [])

        if (isPurchase) {
            next.loansize = `${Math.max(0, appraisalOrSales - down)}`
        } else if (isRefinance) {
            next.loansize = `${Math.max(0, mortgagebalance - paydown)}`
        } else if (isCashout) {
            next.loansize = `${mortgagebalance + cashout}`
            next.mincashout = `${cashout}`
        }
        return next
    }

    const saveProperty = () => {
        const isPositiveAmount = (value) => Number(value) > 0
        const errors = {}

        if (!property.purpose) {
            errors.purpose = "Please select a loan purpose."
        }
        if (!property.occupancy) {
            errors.occupancy = "Please select how you plan to use the property."
        }
        if (!property.propertytype) {
            errors.propertytype = "Please select a property type."
        }
        if (property.propertytype === "twotofourfamily" || property.propertytype === "commercial") {
            const units = parseInt(property.numberofunits, 10)
            if (!(units >= 1)) {
                errors.numberofunits = "Please enter the number of units."
            } else if (property.propertytype === "twotofourfamily" && units > 4) {
                errors.numberofunits = "Multifamily (1-4 unit) has 1 to 4 units. Choose Multifamily (5+ unit) for a larger building."
            } else if (property.propertytype === "commercial" && units < 5) {
                errors.numberofunits = "Multifamily (5+ unit) has 5 or more units."
            }
        }

        // Purpose-dependent rules: gate on purpose so empty purpose does not
        // treat !isPurchase as refinance/cashout (would falsely require address).
        if (property.purpose) {
            if ((isPurchase && property.hascontract) || !isPurchase) {
                if (!property.address) {
                    errors.address = "Please provide address of the property"
                }
            }
            if (isPurchase && property.hascontract === null) {
                errors.hascontract = "Please answer whether you know the property address."
            }
            // A buyer is asked one number, the purchase price (shown from the
            // sales price, or the value if only that is on file); a refinance
            // is asked the estimated home value.
            if (isPurchase) {
                if (!isPositiveAmount(property.salesprice || property.appraisal)) {
                    errors.appraisal = "You need to provide the purchase price."
                }
            } else if (!isPositiveAmount(property.appraisal)) {
                errors.appraisal = "You need to provide the estimated home value."
            }
            if (shouldAskMortgageBalance) {
                const balanceText = `${loanbalance ?? ""}`.trim()
                const balanceNum = Number(balanceText)
                // Cash-out refinance may have $0 current balance (e.g. free-and-clear + cash-out).
                // Rate/term refinance still requires a positive current balance.
                const balanceOk = isCashout
                    ? balanceText !== "" && !Number.isNaN(balanceNum) && balanceNum >= 0
                    : isPositiveAmount(loanbalance)
                if (!balanceOk) {
                    errors.loanbalance = "You need to provide the current mortgage balance on the subject property."
                }
            }
            if (isRefinance && !shouldAskMortgageBalance && totalSubjectMortgageBalance(liabilities?.loans || []) === 0) {
                errors.refinanceFreeAndClear = "You can't refinance the property that you own free and clear!"
            }
            if (!isPositiveAmount(selection[purposeAmountField])) {
                errors.purposeAmount = isPurchase
                    ? "You need to provide the downpayment amount."
                    : isRefinance
                        ? "You need to provide the paydown amount."
                        : "You need to provide the cashout amount."
            }
        }

        const firstErrorKey = FIELD_ORDER.find((key) => !!errors[key]) || null
        if (firstErrorKey) {
            setFieldErrors(errors)
            setTimeout(() => scrollToField(firstErrorKey), 50)
            return
        }
        setFieldErrors({})

        onSave({
            property: {
                ...property,
                ...(isPurchase
                    ? {
                          salesprice: property.salesprice || property.appraisal,
                          appraisal: property.appraisal || property.salesprice,
                      }
                    : {}),
                isManufacturedHome: property.propertytype === "mobilehome",
                mobilehomeinfo: {
                    ...emptyMobileHomeInfo,
                    ...(property.mobilehomeinfo || {}),
                    debtsToBePaid: Array.isArray(property.mobilehomeinfo?.debtsToBePaid)
                        ? property.mobilehomeinfo.debtsToBePaid
                        : [],
                },
            },
            loanbalance,
            financingsameproperty: isPurchase ? false : !!financingsameproperty,
            firsttimebuyer: isPurchase && firsttimebuyer === true,
            selection: buildSelectionForSave(),
            isPreapproval,
            newMortgages: hasNewMortgagesRef.current === false ? [] : newMortgagesRef.current,
        })
    }

    const resetPurpose = (purpose) => {
        setProperty((prev) => ({
            ...prev,
            purpose,
            hascontract: purpose === "purchase",
        }))
        if (purpose === "purchase") {
            setFinancingsameproperty(false)
            setLoanbalance("")
        } else {
            setFirsttimebuyer(false)
        }
        setFieldErrors((prev) => {
            const next = { ...prev }
            delete next.purpose
            delete next.hascontract
            delete next.address
            delete next.loanbalance
            delete next.salesprice
            delete next.purposeAmount
            delete next.refinanceFreeAndClear
            return next
        })
    }

    const propertyAddressFields = (label) => (
        <AddressComponent
            id="xyzuit"
            label={label}
            address={property.address}
            unit={property.unit}
            state={property.state}
            county={property.county}
            city={property.city}
            zip={property.zipcode}
            required
            pattern={PROPERTY_ADDRESS_PATTERN}
            placeholder="Address starting with the house number"
            onAddressLineChange={(value) => setField("address", value)}
            onUnitChange={(value) => setField("unit", value)}
            onStateChange={(value) => setField("state", value)}
            onCountyChange={(value) => setField("county", value)}
            onCityChange={(value) => setField("city", value)}
            onZipChange={(value) => setField("zipcode", value)}
        />
    )

    const purposeAmountLabel = isPurchase
        ? "*What is your down payment?"
        : isRefinance
            ? "*What is the amount of your principal paydown?"
            : "*What is your cashout amount?"

    // A buyer is asked one number, the price; it is also what the home is
    // worth to the application until an appraisal says otherwise.
    const setPurchasePrice = (value) => {
        const next = `${value || ""}`
        setProperty((prev) => ({ ...prev, salesprice: next, appraisal: next }))
        clearFieldError("appraisal")
        clearFieldError("salesprice")
        // A down payment given as a percent is a share of the price, so it
        // follows the price; one given in dollars stays put.
        if (downMode === "percent") {
            const pct = Number(downPercentText) || 0
            const price = Number(next) || 0
            setSelectionField("downpayment", pct > 0 && price > 0 ? `${Math.round((price * pct) / 100)}` : "")
        }
    }

    // The down payment can be typed as dollars or as a percent of the price;
    // the application stores dollars either way.
    const purchasePriceAmount = Number(property.salesprice || property.appraisal || 0) || 0
    const downPaymentAmount = Number(selection.downpayment || 0) || 0
    const [downMode, setDownMode] = useState("amount")
    const [downPercentText, setDownPercentText] = useState("")
    const percentOfPrice = (amount) => (purchasePriceAmount > 0 ? (amount / purchasePriceAmount) * 100 : 0)
    const switchDownMode = (mode) => {
        if (mode === "percent") {
            const pct = percentOfPrice(downPaymentAmount)
            setDownPercentText(pct > 0 ? `${Math.round(pct * 100) / 100}` : "")
        }
        setDownMode(mode)
    }
    const setDownPercent = (text) => {
        const cleaned = moneyTypingValue(text, 2)
        // A share of the price cannot be more than all of it.
        if ((Number(cleaned) || 0) > 100) return
        setDownPercentText(cleaned)
        const pct = Number(cleaned) || 0
        if (purchasePriceAmount > 0) {
            setSelectionField("downpayment", pct > 0 ? `${Math.round((purchasePriceAmount * pct) / 100)}` : "")
        }
    }
    const money = (n) => `$${Math.round(n).toLocaleString("en-US")}`
    const downPaymentHint = !isPurchase
        ? ""
        : downMode === "amount"
            ? purchasePriceAmount > 0 && downPaymentAmount > 0
                ? `${Math.round(percentOfPrice(downPaymentAmount) * 10) / 10}% of ${money(purchasePriceAmount)}`
                : ""
            : purchasePriceAmount <= 0
                ? "Enter the purchase price first."
                : downPaymentAmount > 0
                    ? `= ${money(downPaymentAmount)}`
                    : ""

    return (
        <>
            <div className="text-left application-v2-form-body newInterviewPaneProperty">
                <Row>
                    <Col md={12} xs={12} className="mt-4 pl-0">
                        <Form.Group className="inputSection" data-field="purpose">
                            <Form.Label>*Loan purpose</Form.Label>
                            <CheckGroupWithImage
                                className="check-group-wrap property-v2-image-cards"
                                data={[
                                    { value: "purchase", label: "Buy a property", image: "/images/buyproperty.svg" },
                                    { value: "refinance", label: "Refinance my home", image: "/images/refinance.svg" },
                                    { value: "cashoutrefinance", label: "Get cash-out", image: "/images/cashout.svg" },
                                ]}
                                type="radio"
                                direction="horizontal"
                                style={{ width: "100%" }}
                                onChangeSelect={resetPurpose}
                                value={property.purpose}
                            />
                            <FieldError message={fieldErrors.purpose} />
                        </Form.Group>
                    </Col>
                </Row>

                {isPurchase && (
                    <Row>
                        <Col md={6} xs={12} className="mt-4 pl-0">
                            <Form.Group className="inputSection">
                                <Form.Label>
                                    Are you a first-time homebuyer?
                                    <Tips
                                        placement="bottom"
                                        title="What is first time buyer?"
                                        content={<div>A first time home buyer is someone who meets any of the following conditions:<ul><li>An individual who has not owned a principal residence for three years. If you've owned a home but your spouse has not, then you can purchase a place together as first-time homebuyers.</li><li>A single parent who has only owned a home with a former spouse while married.</li><li>A displaced homemaker who has only owned with a spouse.</li><li>An individual who has only owned a principal residence not permanently affixed to a permanent foundation in accordance with applicable regulations.</li><li>An individual who has only owned a property that was not in compliance with state, local, or model building codes and that cannot be brought into compliance for less than the cost of constructing a permanent structure.</li></ul></div>}
                                    />
                                </Form.Label>
                                <CheckGroup
                                    data={[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }]}
                                    onChangeSelect={(value) => setFirsttimebuyer(yesNoToBool(value))}
                                    value={firsttimebuyer === true ? "yes" : "no"}
                                    type="radio"
                                    direction="horizontal"
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                )}

                <div className="mt-4" style={{ paddingRight: 15 }}>
                    <Form.Group className="inputSection" data-field="occupancy">
                        <Form.Label>*How do you plan to use the property?</Form.Label>
                        <CheckGroup
                            data={occupancyOptions}
                            onChangeSelect={(value) => setField("occupancy", value)}
                            value={property.occupancy}
                            type="radio"
                            direction="horizontal"
                        />
                        <FieldError message={fieldErrors.occupancy} />
                    </Form.Group>
                </div>

                <div className="mt-4" style={{ paddingRight: 15 }}>
                    <Form.Group className="inputSection" data-field="propertytype">
                        <Form.Label>
                            *What is your expected property type?
                            <Tips
                                placement="bottom"
                                title="What are property types?"
                                content={<div>Single family, PUD (planned unit development), multifamily, condo, or manufactured home.</div>}
                            />
                        </Form.Label>
                        <CheckGroupWithImage
                            className="check-group-wrap property-v2-image-cards property-v2-property-type"
                            data={[
                                { value: "singlefamilydetached", label: "Single family", image: "/images/singleFamily2.png", width: 38, height: 38 },
                                { value: "puddetached", label: "PUD", image: "/images/townhouse2.png", width: 38, height: 38 },
                                { value: "twotofourfamily", label: "Multifamily (1-4 unit)", image: "/images/multiFamily2.png", width: 47, height: 47 },
                                { value: "commercial", label: "Multifamily (5+ unit)", image: "/images/multiFamily2.png", width: 47, height: 47 },
                                { value: "condominium", label: "Condo", image: "/images/condo2.png", width: 38, height: 38 },
                                { value: "mobilehome", label: "Manufactured home", image: "/images/mobilehome.png", width: 38, height: 38 },
                            ]}
                            onChangeSelect={setPropertyType}
                            value={propertyTypeCardValue}
                            type="radio"
                            hideCircle={true}
                            direction="horizontal"
                            style={{ width: "100%" }}
                        />
                        <FieldError message={fieldErrors.propertytype} />
                    </Form.Group>
                </div>

                {isMultifamily && (
                    <Row>
                        <Col className="pl-0 mt-4" md={6} xs={12} data-field="numberofunits">
                            <Form.Group className="inputSection">
                                <Form.Label>{isLargeMultifamily ? "*Number of units (5 or more)" : "*Number of units (1 to 4)"}</Form.Label>
                                <Input
                                    size="large"
                                    inputMode="numeric"
                                    maxLength={3}
                                    value={property.numberofunits}
                                    status={fieldErrors.numberofunits ? "error" : undefined}
                                    onChange={(event) => {
                                        setField("numberofunits", event.target.value.replace(/\D/g, ""))
                                        clearFieldError("numberofunits")
                                    }}
                                />
                                <FieldError message={fieldErrors.numberofunits} />
                            </Form.Group>
                        </Col>
                    </Row>
                )}

                {property.propertytype === "mobilehome" && (
                    <div>
                        <Row>
                            <Col className="pl-0 mt-4" md={12} xs={12}>
                                <Form.Group className="inputSection">
                                    <Form.Label>What is the full name of the manufactured home park?</Form.Label>
                                    <Form.Control
                                        as="input"
                                        size="sm"
                                        value={property.mobilehomeinfo?.mobilehomeparkfullname || ""}
                                        onChange={(event) => setMobileHomeField("mobilehomeparkfullname", event.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col className="pl-0 mt-4" md={12} xs={12}>
                                <Form.Group className="text-left">
                                    <AddressComponent
                                        id="parkAddress"
                                        label="What's the address of the park?"
                                        address={property.mobilehomeinfo?.parkStreet || property.mobilehomeinfo?.parkAddress || ""}
                                        unit={property.mobilehomeinfo?.parkUnit}
                                        state={property.mobilehomeinfo?.parkState}
                                        county={property.mobilehomeinfo?.parkCounty}
                                        city={property.mobilehomeinfo?.parkCity}
                                        zip={property.mobilehomeinfo?.parkZip}
                                        placeholder="Address starting with the house number"
                                        onAddressLineChange={(value) => setMobileHomeAddressField("parkStreet", value)}
                                        onUnitChange={(value) => setMobileHomeAddressField("parkUnit", value)}
                                        onStateChange={(value) => setMobileHomeAddressField("parkState", value)}
                                        onCountyChange={(value) => setMobileHomeAddressField("parkCounty", value)}
                                        onCityChange={(value) => setMobileHomeAddressField("parkCity", value)}
                                        onZipChange={(value) => setMobileHomeAddressField("parkZip", value)}
                                        pattern={PROPERTY_ADDRESS_PATTERN}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col className="pl-0 mt-4" md={12} xs={12}>
                                <Form.Group className="inputSection">
                                    <Form.Label>What is the monthly space rent for the manufactured home park?</Form.Label>
                                    <DollarInputV2
                                        value={property.mobilehomeinfo?.leasedSpaceRentAmount || ""}
                                        onChange={(value) => setMobileHomeField("leasedSpaceRentAmount", `${value || ""}`)}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col className="pl-0 mt-4" md={6}>
                                <Form.Group className="inputSection">
                                    <Form.Label>Width of the manufactured home</Form.Label>
                                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                                        <div style={{ width: "49%" }}>
                                            <Input
                                                value={property.mobilehomeinfo?.widthft || ""}
                                                onChange={(event) => setMobileHomeField("widthft", event.target.value)}
                                                suffix="ft"
                                                size="large"
                                            />
                                        </div>
                                        <div style={{ width: "49%" }}>
                                            <Input
                                                value={property.mobilehomeinfo?.widthinch || ""}
                                                onChange={(event) => setMobileHomeField("widthinch", event.target.value)}
                                                suffix="inch"
                                                size="large"
                                            />
                                        </div>
                                    </div>
                                </Form.Group>
                            </Col>
                            <Col className="pl-0 mt-4" md={6}>
                                <Form.Group className="inputSection">
                                    <Form.Label>Length of the manufactured home</Form.Label>
                                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                                        <div style={{ width: "49%" }}>
                                            <Input
                                                value={property.mobilehomeinfo?.lengthft || ""}
                                                onChange={(event) => setMobileHomeField("lengthft", event.target.value)}
                                                suffix="ft"
                                                size="large"
                                            />
                                        </div>
                                        <div style={{ width: "49%" }}>
                                            <Input
                                                value={property.mobilehomeinfo?.lengthinch || ""}
                                                onChange={(event) => setMobileHomeField("lengthinch", event.target.value)}
                                                suffix="inch"
                                                size="large"
                                            />
                                        </div>
                                    </div>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col className="pl-0 mt-4" md={6} xs={12}>
                                <Form.Group className="inputSection">
                                    <Form.Label>What year is the manufactured home built?</Form.Label>
                                    <Input
                                        type="number"
                                        value={property.mobilehomeinfo?.year || ""}
                                        onChange={(event) => setMobileHomeField("year", `${event.target.value || ""}`.slice(0, 4))}
                                        size="large"
                                    />
                                </Form.Group>
                            </Col>
                            <Col className="pl-0 mt-4">
                                <Form.Group className="inputSection">
                                    <Form.Label>Space number in the manufactured home park</Form.Label>
                                    <Form.Control
                                        as="input"
                                        size="sm"
                                        value={property.mobilehomeinfo?.space || ""}
                                        onChange={(event) => setMobileHomeField("space", event.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col className="pl-0 mt-4" md={6} xs={12}>
                                <Form.Group className="inputSection">
                                    <Form.Label>Condition of manufactured home</Form.Label>
                                    <Select
                                        className="w-100"
                                        style={{ height: 44 }}
                                        value={property.mobilehomeinfo?.mobilehomedescription || undefined}
                                        onChange={(value) => setMobileHomeField("mobilehomedescription", value)}
                                        options={[
                                            { value: "New", label: "New" },
                                            { value: "Used", label: "Used" },
                                            { value: "Repo", label: "Repo" },
                                        ]}
                                    />
                                </Form.Group>
                            </Col>
                            <Col className="pl-0 mt-4" md={6} xs={12}>
                                <Form.Group className="inputSection">
                                    <Form.Label>Type of manufactured home</Form.Label>
                                    <Select
                                        className="w-100"
                                        style={{ height: 44 }}
                                        value={property.mobilehomeinfo?.mobilehometype || undefined}
                                        onChange={(value) => setMobileHomeField("mobilehometype", value)}
                                        options={[
                                            { value: "SingleWide", label: "Single Wide" },
                                            { value: "DoubleWide", label: "Double Wide" },
                                            { value: "TripleWide", label: "Triple Wide" },
                                        ]}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col className="mt-4 pl-0" md={6} xs={12}>
                                <Form.Group className="inputSection">
                                    <Form.Label>Location of manufactured home</Form.Label>
                                    <Select
                                        className="w-100"
                                        style={{ height: 44 }}
                                        value={property.mobilehomeinfo?.mobilehomelocation || undefined}
                                        onChange={(value) => setMobileHomeField("mobilehomelocation", value)}
                                        options={[
                                            { value: "Park", label: "Park" },
                                            { value: "LandorHome", label: "Land/home" },
                                            { value: "CoopPark", label: "Co-op park" },
                                            { value: "SharedPark", label: "Shared park" },
                                        ]}
                                    />
                                </Form.Group>
                            </Col>
                            {!isPurchase && (
                                <Col className="pl-0 mt-4" md={6} xs={12}>
                                    <Form.Group className="inputSection">
                                        <Form.Label>Original purchase price</Form.Label>
                                        <DollarInputV2
                                            value={property.mobilehomeinfo?.originalpurchaseprice || ""}
                                            onChange={(value) => setMobileHomeField("originalpurchaseprice", `${value || ""}`)}
                                        />
                                    </Form.Group>
                                </Col>
                            )}
                        </Row>
                        <Row>
                            <Col className="pl-0 mt-4" md={6} xs={12}>
                                <Form.Group className="inputSection">
                                    <Form.Label>Manufacturer make</Form.Label>
                                    <Form.Control
                                        as="input"
                                        size="sm"
                                        value={property.mobilehomeinfo?.manufacturerMake || ""}
                                        onChange={(event) => setMobileHomeField("manufacturerMake", event.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                            <Col className="pl-0 mt-4" md={6} xs={12}>
                                <Form.Group className="inputSection">
                                    <Form.Label>Manufacturer model</Form.Label>
                                    <Form.Control
                                        as="input"
                                        size="sm"
                                        value={property.mobilehomeinfo?.manufacturerModel || ""}
                                        onChange={(event) => setMobileHomeField("manufacturerModel", event.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        {isCashout && (
                            <Row>
                                <Col className="pl-0 mt-4" md={6} xs={12}>
                                    <Form.Group className="inputSection">
                                        <Form.Label>Cash out requested</Form.Label>
                                        <DollarInputV2
                                            value={property.mobilehomeinfo?.cashOutRequested || ""}
                                            onChange={(value) => setMobileHomeField("cashOutRequested", `${value || ""}`)}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        )}
                        {!isPurchase && (
                            <Row>
                                <Col className="pl-0 mt-4" md={12}>
                                    <EditableTable
                                        dataSource={property.mobilehomeinfo?.debtsToBePaid || []}
                                        title="Add debts to be paid"
                                        limitMoneyDecimals
                                        columns={[
                                            { title: "Description of the debt", dataIndex: "description", type: "input" },
                                            { title: "Remaining debt amount", dataIndex: "amount", type: "inputdollar" },
                                        ]}
                                        newDataTemplate={{ amount: "", description: "" }}
                                        saveFunc={(rows) => setMobileHomeField("debtsToBePaid", rows)}
                                    />
                                </Col>
                            </Row>
                        )}
                    </div>
                )}

                {isPurchase && (
                    <>
                        <Row>
                            <Col md={12} xs={12} className="mt-4 pl-0">
                                <Form.Label>
                                    *Do you know the address of the property you are going to purchase?
                                    <Tips
                                        placement="top"
                                        title="What if I don't know the property address yet?"
                                        content={<div><div>If you don't have a property address yet, you'll receive a prequalification letter that demonstrates your borrowing potential.</div><div className="mt-2">When you have a specific property address, you can come back and get personalized rates.</div></div>}
                                    />
                                </Form.Label>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6} xs={12} className="pl-0">
                                <Form.Group className="inputSection" data-field="hascontract">
                                    <CheckGroup
                                        data={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                                        onChangeSelect={(value) => {
                                            setProperty((prev) => ({
                                                ...prev,
                                                hascontract: value === "true",
                                                address: value === "false" ? "" : prev.address,
                                            }))
                                            clearFieldError("hascontract")
                                            if (value === "false") {
                                                clearFieldError("address")
                                                clearFieldError("salesprice")
                                            }
                                        }}
                                        value={property.hascontract === true ? "true" : property.hascontract === false ? "false" : ""}
                                        type="radio"
                                        direction="horizontal"
                                    />
                                    <FieldError message={fieldErrors.hascontract} />
                                </Form.Group>
                            </Col>
                        </Row>
                    </>
                )}

                {isPurchase && property.hascontract && (
                    <Row>
                        <Col xs={12} className="mt-4 mb-2 pl-0" data-field="address">
                            {propertyAddressFields("*What's the address of the property you are going to purchase?")}
                            <FieldError message={fieldErrors.address} />
                        </Col>
                    </Row>
                )}

                {!isPurchase && (
                    <Row>
                        <Col className="mt-4 pl-0" xs={12} data-field="address">
                            <Form.Group className="text-left">
                                {propertyAddressFields("*What's the address of the property you are going to refinance?")}
                                <FieldError message={fieldErrors.address || fieldErrors.refinanceFreeAndClear} />
                                {showFinancingSameProperty && (
                                    <Row>
                                        <Col md={6} xs={12} className="pl-0 pt-2">
                                            <Checkbox
                                                className="applicant-checkbox"
                                                checked={!!financingsameproperty}
                                                onChange={(event) => handleFinancingSameProperty(event.target.checked)}
                                            >
                                                <span className="wrap-text" style={{ fontSize: 16, fontWeight: 500, color: "#222222" }}>
                                                    Same as my current residence.
                                                </span>
                                            </Checkbox>
                                        </Col>
                                    </Row>
                                )}
                            </Form.Group>
                        </Col>
                    </Row>
                )}

                {property.occupancy === "principal" && (
                    <>
                        <Row className="mt-4">
                            <Col className="pl-0" md={12} xs={12}>
                                <Form.Label>Is part of this property going to be used for business?</Form.Label>
                            </Col>
                        </Row>
                        <Row>
                            <Col className="pl-0" md={6} xs={12}>
                                <Form.Group className="inputSection">
                                    <CheckGroup
                                        className="property-v2-yesno"
                                        data={[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }]}
                                        onChangeSelect={(value) => setField("isMixedUse", value === "yes")}
                                        value={boolToYesNo(property.isMixedUse)}
                                        type="radio"
                                        direction="horizontal"
                                        style={{ width: "100%" }}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </>
                )}

                {property.occupancy === "secondhome" && (
                    <Row className="mt-4">
                        <Col className="pl-0" md={6} xs={12}>
                            <Form.Group className="inputSection">
                                <Form.Label>
                                    Do you intend to use the second home for work related purposes?
                                    <Tips
                                        placement="top"
                                        title="FHA loans only"
                                        content={<div>This is only important for FHA loans that have a separate category of FHA second home. For non-FHA loans, select No.</div>}
                                    />
                                </Form.Label>
                                <CheckGroup
                                    data={[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }]}
                                    onChangeSelect={(value) => setField("qualifiesForFHAsecondary", value === "yes")}
                                    value={boolToYesNo(property.qualifiesForFHAsecondary)}
                                    type="radio"
                                    direction="horizontal"
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                )}

                {shouldAskMortgageBalance && (
                    <>
                        <Row>
                            <Col className="text-wrap pl-0 pr-2 mt-4" xs={12}>
                                <Form.Label>*What's the current mortgage balance?</Form.Label>
                            </Col>
                        </Row>
                        <Row>
                            <Col className="text-wrap pl-0 pr-3" md={6} xs={12} data-field="loanbalance">
                                <Form.Group className="inputSection">
                                    <DollarInputV2
                                        value={loanbalance == null || loanbalance === "" ? "" : `${loanbalance}`}
                                        onChange={setLoanbalanceValue}
                                    />
                                    <FieldError message={fieldErrors.loanbalance} />
                                </Form.Group>
                            </Col>
                        </Row>
                    </>
                )}

                <Row className="property-address">
                    <Col md={6} xs={12} className="mt-4" data-field="appraisal">
                        <Form.Group className="inputSection">
                            {isPurchase ? (
                                <>
                                    <Form.Label>*What's your purchase price?</Form.Label>
                                    <DollarInputV2 value={property.salesprice || property.appraisal} onChange={(value) => setPurchasePrice(value)} required />
                                </>
                            ) : (
                                <>
                                    <Form.Label>*What's your estimated home value?</Form.Label>
                                    <DollarInputV2 value={property.appraisal} onChange={(value) => setField("appraisal", `${value || ""}`)} required />
                                </>
                            )}
                            <FieldError message={fieldErrors.appraisal} />
                        </Form.Group>
                    </Col>
                    <Col md={6} xs={12} className="mt-4" data-field="purposeAmount">
                        <Form.Group className="inputSection">
                            {isPurchase ? (
                                <>
                                    <Form.Label>{purposeAmountLabel}</Form.Label>
                                    <div className="v2-money-row">
                                        {downMode === "amount" ? (
                                            <DollarInputV2 value={selection.downpayment} onChange={(value) => setSelectionField("downpayment", `${value || ""}`)} required />
                                        ) : (
                                            <Input
                                                size="large"
                                                className="w-100"
                                                suffix="%"
                                                inputMode="decimal"
                                                value={downPercentText}
                                                onChange={(e) => setDownPercent(e.target.value)}
                                                required
                                            />
                                        )}
                                        <span className="v2-money-mode" role="group" aria-label="Enter the down payment as an amount or a percent">
                                            <button type="button" className={downMode === "amount" ? "active" : ""} onClick={() => switchDownMode("amount")}>$</button>
                                            <button type="button" className={downMode === "percent" ? "active" : ""} onClick={() => switchDownMode("percent")}>%</button>
                                        </span>
                                    </div>
                                    {downPaymentHint && <div className="v2-money-hint">{downPaymentHint}</div>}
                                    <FieldError message={fieldErrors.purposeAmount} />
                                </>
                            ) : (
                                <>
                                    <Form.Label>{purposeAmountLabel}</Form.Label>
                                    <DollarInputV2 value={selection[purposeAmountField]} onChange={(value) => setSelectionField(purposeAmountField, `${value || ""}`)} required />
                                    <FieldError message={fieldErrors.purposeAmount} />
                                </>
                            )}
                        </Form.Group>
                    </Col>
                    {(property.occupancy === "investment" || property.propertytype === "twotofourfamily") && (
                        <Col md={6} xs={12} className="mt-4">
                            <Form.Group className="inputSection">
                                <Form.Label>Expected Monthly Rental Income</Form.Label>
                                <DollarInputV2
                                    className="w-100"
                                    value={property.netrentalincome}
                                    onChange={(value) => setField("netrentalincome", value == null ? "" : `${value}`)}
                                />
                            </Form.Group>
                        </Col>
                    )}
                </Row>

                {!isPreapproval && (
                    <>
                        <Row>
                            <Col md={12} xs={12} className="mt-4 pl-0">
                                <Form.Label className="d-flex justify-content-between text-wrap">Do you have any new mortgage loans for the property that you're applying for now?</Form.Label>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6} xs={12} className="pl-0">
                                <Form.Group className="inputSection">
                                    <CheckGroup
                                        data={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                                        value={hasNewMortgages === null ? "" : hasNewMortgages ? "true" : "false"}
                                        onChangeSelect={(value) => value === "true" ? (newMortgages.length === 0 ? addNewMortgage() : setNewMortgageAnswer(true, newMortgagesRef.current)) : setNewMortgageAnswer(false, [])}
                                        type="radio"
                                        direction="horizontal"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        {newMortgages.length > 0 && (
                            <div className="new-mortgage-section">
                                {newMortgages.map((mortgage, index) => (
                                    <div className="incard mt-2 mb-0 new-mortgage-card" key={mortgage.key || index}>
                                        <div className="new-mortgage-scroll">
                                            <div className="new-mortgage-fields">
                                                <div className="new-mortgage-field new-mortgage-field-creditor">
                                                    <Form.Group>
                                                        <Form.Label title="Creditor">Creditor</Form.Label>
                                                        <Form.Control value={mortgage.creditor || ""} onChange={(event) => updateNewMortgage(index, { creditor: event.target.value })} title={mortgage.creditor || ""} />
                                                    </Form.Group>
                                                </div>
                                                <div className="new-mortgage-field new-mortgage-field-lien">
                                                    <Form.Group>
                                                        <Form.Label title="Lien type">Lien type</Form.Label>
                                                        <Form.Control as="select" value={mortgage.lienType || "firstlien"} onChange={(event) => updateNewMortgage(index, { lienType: event.target.value })}>
                                                            <option value="firstlien">First lien</option>
                                                            <option value="subordinatelien">Subordinate lien</option>
                                                        </Form.Control>
                                                    </Form.Group>
                                                </div>
                                                <div className="new-mortgage-field new-mortgage-field-amount">
                                                    <Form.Group>
                                                        <Form.Label title="Monthly amount">Monthly amount</Form.Label>
                                                        <DollarInputV2 value={mortgage.monthlyAmount || ""} onChange={(value) => updateNewMortgage(index, { monthlyAmount: `${value || ""}` })} />
                                                    </Form.Group>
                                                </div>
                                                <div className="new-mortgage-field new-mortgage-field-amount">
                                                    <Form.Group>
                                                        <Form.Label title="Loan amount">Loan amount</Form.Label>
                                                        <DollarInputV2 value={mortgage.loanAmount || ""} onChange={(value) => updateNewMortgage(index, { loanAmount: `${value || ""}` })} />
                                                    </Form.Group>
                                                </div>
                                                <div className="new-mortgage-field new-mortgage-field-limit">
                                                    <Form.Group>
                                                        <Form.Label title="Credit limit">Credit limit<Tips content={<div>Input 0 if N/A</div>} /></Form.Label>
                                                        <DollarInputV2 value={mortgage.creditLimit || ""} onChange={(value) => updateNewMortgage(index, { creditLimit: `${value || ""}` })} />
                                                    </Form.Group>
                                                </div>
                                                <div className="new-mortgage-actions">
                                                    {index === newMortgages.length - 1 && <AntButton type="link" onClick={addNewMortgage}>+</AntButton>}
                                                    <AntButton type="link" onClick={() => {
                                                        const nextItems = newMortgages.filter((_, itemIndex) => itemIndex !== index)
                                                        setNewMortgageAnswer(nextItems.length > 0, nextItems)
                                                    }}>-</AntButton>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>

            <Row className="text-left application-bottom-bar">
                <div className="d-flex align-items-center">
                    <BootstrapButton type="button" variant="zeitro-primary" disabled={saving} className="operate-btn back mr-3" onClick={onBack}>
                        Back
                    </BootstrapButton>
                    <BootstrapButton
                        type="button"
                        variant="zeitro-primary"
                        disabled={saving}
                        className="text-center operate-btn btn"
                        onClick={saveProperty}
                    >
                        Next
                    </BootstrapButton>
                </div>
            </Row>
        </>
    )
}

export default PropertyV2
